import React, { useEffect, useState } from 'react';
import { CheckCircle2, ArrowRight, Zap, Users, BarChart3, ShieldCheck, Settings, Layers, Clock, UserPlus, ArrowRightLeft, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getStaff } from '../services/staffService';
import { getSettings } from '../services/settingsService';
import { getRolePercentages } from '../services/roleService';
import { getShifts } from '../services/shiftService';
import { getUsersByTenant } from '../services/userService';
import { getTenant } from '../services/tenantService';

interface Props {
  tenantId: string;
}

export const LaunchChecklist: React.FC<Props> = ({ tenantId }) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const [activePhaseId, setActivePhaseId] = useState(1);
  const [steps, setSteps] = useState({
    general: false,
    payout: false,
    roles: false,
    shifts: false,
    staff: false,
    users: false,
    importMapping: false,
    exportMapping: false,
    plan: false,
    loading: true
  });

  useEffect(() => {
    const checkProgress = async () => {
      try {
        const [settings, staff, roles, shifts, users, tenant] = await Promise.all([
          getSettings(tenantId),
          getStaff(tenantId),
          getRolePercentages(tenantId),
          getShifts(tenantId),
          getUsersByTenant(tenantId),
          getTenant(tenantId)
        ]);

        setSteps({
          general: !!(settings?.allocationMethod && settings?.timezone && tenant?.name),
          payout: !!(settings?.payoutMethod), 
          roles: roles.length > 0,
          shifts: shifts.length > 0,
          staff: staff.length > 0,
          users: users.length > 1, 
          importMapping: !!(settings?.posMapping && settings?.posMapping.posId && settings?.posMapping.amount && settings?.posMapping.tipDate),
          exportMapping: !!(settings?.payrollProvider && settings?.payrollProvider !== ''),
          plan: tenant?.subscriptionStatus !== 'trialing',
          loading: false
        });
      } catch (error) {
        console.error("Setup Check Error:", error);
        setSteps(prev => ({ ...prev, loading: false }));
      }
    };
    checkProgress();
  }, [tenantId]);

  // Automatically switch to the first incomplete phase once steps are loaded
  useEffect(() => {
    if (!steps.loading) {
      if (!steps.general || !steps.payout || !steps.shifts) {
        setActivePhaseId(1);
      } else if (!steps.roles || !steps.users || !steps.staff) {
        setActivePhaseId(2);
      } else if (!steps.importMapping || !steps.exportMapping || !steps.plan) {
        setActivePhaseId(3);
      } else {
        setActivePhaseId(1);
      }
    }
  }, [steps.loading, steps.general, steps.payout, steps.shifts, steps.roles, steps.users, steps.staff, steps.importMapping, steps.exportMapping, steps.plan]);

  if (steps.loading) return (
    <div className="h-48 bg-gray-900/5 rounded-[2.5rem] animate-pulse flex items-center justify-center">
      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Scanning Launch Sequence...</p>
    </div>
  );

  const completedCount = Object.values(steps).filter(v => v === true).length;
  const totalCount = 9;
  const progressPercent = Math.round((completedCount / totalCount) * 100);

  if (progressPercent === 100 && !isDismissed) {
    return (
      <div className="bg-emerald-600 rounded-xl p-4 text-white flex items-center justify-between gap-4 shadow-lg animate-in fade-in zoom-in-95 duration-500">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center shrink-0">
            <ShieldCheck size={16} />
          </div>
          <div>
            <h3 className="font-black uppercase italic tracking-tighter leading-none text-sm">Vessel Flight-Ready</h3>
            <p className="opacity-80 font-medium text-[10px] mt-0.5">TipValue is fully operational.</p>
          </div>
        </div>
        <button onClick={() => setIsDismissed(true)} className="hover:bg-white/20 p-1 rounded-full transition-colors">
          <X size={16} />
        </button>
      </div>
    );
  }

  if (progressPercent === 100) return null;

  const phaseSteps = [
    {
      id: 'general',
      phaseId: 1,
      icon: <Settings size={20} />,
      title: "Business Profile",
      description: "Timezone & Name",
      isDone: steps.general,
      link: "/settings/onboarding"
    },
    {
      id: 'payout',
      phaseId: 1,
      icon: <Zap size={20} />,
      title: "Payout Strategy",
      description: "Methods & Overrides",
      isDone: steps.payout,
      link: "/settings/general"
    },
    {
      id: 'shifts',
      phaseId: 1,
      icon: <Clock size={20} />,
      title: "Shift Windows",
      description: "Wait Periods",
      isDone: steps.shifts,
      link: "/settings/shifts"
    },
    {
      id: 'roles',
      phaseId: 2,
      icon: <Layers size={20} />,
      title: "Role Allocation Settings",
      description: "Tip Percentages",
      isDone: steps.roles,
      link: "/settings/allocations"
    },
    {
      id: 'users',
      phaseId: 2,
      icon: <UserPlus size={20} />,
      title: "Invite Users",
      description: "Invite Managers",
      isDone: steps.users,
      link: "/user-management"
    },
    {
      id: 'staff',
      phaseId: 2,
      icon: <Users size={20} />,
      title: "Staff Roster",
      description: "Add Workforce",
      isDone: steps.staff,
      link: "/settings/staff"
    },
    {
      id: 'importMapping',
      phaseId: 3,
      icon: <ArrowRightLeft size={20} />,
      title: "Import Mapping",
      description: "POS Field Sync",
      isDone: steps.importMapping,
      link: "/pos-import"
    },
    {
      id: 'exportMapping',
      phaseId: 3,
      icon: <BarChart3 size={20} />,
      title: "Export Mapping",
      description: "Payroll Formatting",
      isDone: steps.exportMapping,
      link: "/payroll-export"
    },
    {
      id: 'plan',
      phaseId: 3,
      icon: <ShieldCheck size={20} />,
      title: "Plan Selection",
      description: steps.plan ? "Subscription Active" : "Open Trial",
      isDone: steps.plan,
      link: "/settings/billing"
    }
  ];

  return (
    <div className="bg-gray-900 rounded-2xl p-6 text-white relative overflow-hidden group border border-white/10 shadow-lg">
      {/* Background patterns */}
      <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-all duration-1000 rotate-12">
        <Settings size={180} />
      </div>

      <div className="relative z-10 space-y-10">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 border-b border-white/5 pb-6">
          <div className="space-y-2">
            <h3 className="text-4xl font-black uppercase italic tracking-tighter leading-none">Setup Checklist</h3>
            <p className="text-gray-400 font-medium text-xs max-w-md">Configure your core parameters to enable automated tip distributions and exports across incremental operational milestones.</p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            {/* Phase selectors */}
            <div className="grid grid-cols-3 gap-1 bg-white/[0.03] p-1 rounded-2xl border border-white/5 sm:w-80">
              {[1, 2, 3].map((pId) => {
                const phaseConfig = {
                  1: { name: 'Foundation' },
                  2: { name: 'Workspace' },
                  3: { name: 'Live & Sync' }
                }[pId as 1 | 2 | 3];
                
                const stepsInPhase = phaseSteps.filter(s => s.phaseId === pId);
                const completedInPhase = stepsInPhase.filter(s => s.isDone).length;
                const isSelected = activePhaseId === pId;
                
                return (
                  <button
                    key={pId}
                    type="button"
                    onClick={() => setActivePhaseId(pId)}
                    className={`py-2 px-1 rounded-xl flex flex-col items-center justify-center gap-0.5 transition-all relative ${
                      isSelected 
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' 
                        : 'text-gray-400 hover:text-white hover:bg-white/[0.02]'
                    }`}
                  >
                    <span className="text-[9px] font-black uppercase tracking-wider">{phaseConfig.name}</span>
                    <span className="text-[8px] opacity-80 font-black">
                      {completedInPhase}/{stepsInPhase.length} Done
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="bg-white/5 rounded-2xl p-3 px-5 border border-white/10 flex items-center justify-between gap-4 shrink-0">
              <div>
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none">Overall Progress</div>
                <div className="text-xl font-black italic tracking-tighter text-emerald-400 mt-1.5 leading-none">{progressPercent}%</div>
              </div>
              <div className="w-16 h-1 bg-white/10 rounded-full overflow-hidden">
                 <div 
                   className="h-full bg-emerald-500 transition-all duration-1000" 
                   style={{ width: `${progressPercent}%` }} 
                 />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {phaseSteps
            .filter(step => step.phaseId === activePhaseId)
            .map(step => (
              <CheckStep 
                key={step.id}
                icon={step.icon}
                title={step.title}
                description={step.description}
                isDone={step.isDone}
                link={step.link}
              />
            ))
          }
        </div>
      </div>
    </div>
  );
};

interface StepProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  isDone: boolean;
  link: string;
}

const CheckStep: React.FC<StepProps> = ({ icon, title, description, isDone, link }) => (
  <Link 
    to={link}
    className={`p-6 rounded-[2rem] border transition-all duration-500 flex flex-col items-center text-center space-y-4 group/step relative overflow-hidden ${
      isDone 
      ? 'border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10' 
      : 'border-white/5 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/10'
    }`}
  >
    {isDone && (
      <div className="absolute top-0 right-0 p-2">
        <CheckCircle2 size={16} className="text-emerald-500" />
      </div>
    )}
    
    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-500 ${
      isDone ? 'bg-emerald-500 text-white rotate-[360deg]' : 'bg-white/5 text-gray-500 group-hover/step:bg-white/10 group-hover/step:text-white'
    }`}>
      {icon}
    </div>
    
    <div className="space-y-1">
      <h4 className={`font-black uppercase tracking-tight text-sm transition-colors ${isDone ? 'text-white' : 'text-gray-400 group-hover/step:text-white'}`}>{title}</h4>
      <p className="text-[10px] uppercase font-bold text-gray-600 tracking-widest">{description}</p>
    </div>

    <div className="mt-auto w-full pt-4">
      {isDone ? (
        <div className="flex items-center justify-center gap-2 text-emerald-500 text-[10px] font-black uppercase tracking-[0.2em] opacity-80 underline decoration-2 underline-offset-4">
          Edit Config
        </div>
      ) : (
        <div className="flex items-center justify-center gap-2 text-gray-500 text-[10px] font-black uppercase tracking-[0.2em] group-hover/step:text-blue-400 transition-colors">
          Start <ArrowRight size={14} className="group-hover/step:translate-x-1 transition-transform" />
        </div>
      )}
    </div>
  </Link>
);
